// What a card or a row shows of an item, read through the roles of its type's
// fields (title, year, creators, subtitle, cover) — never through field
// names. Without a role, the same fallbacks as the server's index
// (ItemIndex): a key `title`, else the first text field; a key `year`.
import { asTags } from '../../fieldTypes';
import { fieldKey } from '../../fieldKeys';
import { usedFields } from '../../kinds';

const roleField = (fields, role) => (fields || []).find((field) => field.role === role && !field.unused);

const text = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.filter((v) => v !== null && v !== '').join(', ');
  if (typeof value === 'object') return '';
  return String(value).trim();
};

const yearFrom = (value) => {
  if (typeof value === 'number' && Number.isInteger(value)) return value >= 1000 && value <= 9999 ? String(value) : '';
  const match = text(value).match(/\b(\d{4})\b/);
  return match ? match[1] : '';
};

/** {title, subtitle, year, creators} of an item, from its type's fields. */
export function itemSummary(item, fields) {
  const values = (item && item.field_values) || {};
  const used = usedFields(fields);
  const valueOf = (field) => (field ? values[fieldKey(field)] : undefined);

  const titleField = roleField(used, 'title')
    || used.find((field) => fieldKey(field) === 'title')
    || used.find((field) => (field.type || 'text') === 'text');
  const yearField = roleField(used, 'year') || used.find((field) => fieldKey(field) === 'year');
  const creatorsField = roleField(used, 'creators');
  const subtitleField = roleField(used, 'subtitle');

  const creators = creatorsField
    ? (creatorsField.type === 'tags' ? asTags(valueOf(creatorsField)) : [text(valueOf(creatorsField))].filter(Boolean))
    : [];
  return {
    title: text(valueOf(titleField)),
    subtitle: text(valueOf(subtitleField)),
    year: yearFrom(valueOf(yearField)),
    creators,
  };
}

/** "1964 · Stanley Kubrick" — the year and the first two creators, "et al." past them. */
export const metaLine = (summary, more) => {
  const people = summary.creators.slice(0, 2).join(', ') + (summary.creators.length > 2 ? more : '');
  return [summary.year, people].filter(Boolean).join(' · ');
};

/** True when one of the item types shown has a cover (an image field playing the cover role). */
export const typesHaveCovers = (types) => (types || [])
  .some((type) => usedFields(type.fields).some((field) => field.type === 'image' && field.role === 'cover'));

// ---- The view of a collection (grid, list, table), remembered per collection.

export const VIEWS = ['grid', 'list', 'table'];
const viewKey = (inventoryId) => `inventorium.view.${inventoryId}`;

/** The view chosen for this collection, or null (storage may be unavailable). */
export function storedView(inventoryId) {
  try {
    const value = window.localStorage.getItem(viewKey(inventoryId));
    return VIEWS.includes(value) ? value : null;
  } catch (error) {
    return null;
  }
}

export function storeView(inventoryId, view) {
  try {
    window.localStorage.setItem(viewKey(inventoryId), view);
  } catch (error) {
    // Private mode or storage refused: the choice lasts until the page is left.
  }
}

const isPhone = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches;

/**
 * Without a stored choice: covers make a grid, for everyone (readers open
 * items on their page since phase 3); otherwise the table (a list on phones).
 */
export const defaultView = (hasCovers) => (hasCovers ? 'grid' : (isPhone() ? 'list' : 'table'));
